import { observer } from "mobx-react-lite";
import { FC } from "react";

import { DateTime } from "@bps/date-time";
import { DetailsList, Stack } from "@bps/fluent-ui";
import { XeroSettingsAddDto } from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { ToggleField } from "@ui-components/form/Toggle/ToggleField.tsx";

import { useIntegrationScreenContext } from "../../context/IntegrationScreenContext.ts";

type TransactionTypeRow = {
  key: string;
  label: string;
};

const nameOf = nameOfFactory<XeroSettingsFormValues>();

type XeroSettingsFormValues = {
  includeInvoices: boolean;
  includePayments: boolean;
  startDate: Date | undefined;
};

export const XeroSettingsForm: FC = observer(() => {
  const { notification, customIntegrationXero } = useStores();
  const context = useIntegrationScreenContext();

  if (!context.xeroTenantId) return null;

  const xeroTenantId = context.xeroTenantId;

  const handleSubmit = async (values: XeroSettingsFormValues) => {
    const dto: XeroSettingsAddDto = {
      includeInvoices: values.includeInvoices,
      includePayments: values.includePayments,
      startDate: DateTime.fromJSDate(values.startDate)?.toISO(),
      targetTenantId: xeroTenantId
    };

    if (customIntegrationXero.settings) {
      await customIntegrationXero.updateSettings({
        ...dto,
        eTag: customIntegrationXero.settings.eTag
      });
      notification.success("Xero settings have been updated.");
    } else {
      await customIntegrationXero.addSettings(dto);
      notification.success("Xero settings have been saved.");
    }
  };

  const rows: TransactionTypeRow[] = [
    { key: "includeInvoices", label: "Invoices" },
    { key: "includePayments", label: "Payments" }
  ];

  return (
    <SubmissionForm<XeroSettingsFormValues>
      onSubmit={handleSubmit}
      formName="xero-settings-form"
      buttonsProps={{
        disableCancelOnPristine: true,
        submitButtonProps: { iconProps: { iconName: "" } }
      }}
      disabled={customIntegrationXero.settings?.hasSynced}
      initialValues={{
        includeInvoices:
          customIntegrationXero.settings?.includeInvoices ?? true,
        includePayments:
          customIntegrationXero.settings?.includePayments ?? false,
        startDate: DateTime.jsDateFromISO(
          customIntegrationXero.settings?.startDate
        )
      }}
    >
      {({ form, values }) => {
        return (
          <Stack tokens={{ childrenGap: 12, padding: 12 }}>
            <DatePickerField
              name={nameOf("startDate")}
              label="Sync from date"
              maxDate={DateTime.jsDateNow()}
            />
            <Stack styles={{ root: { width: 500 } }}>
              <DetailsList
                items={rows}
                columns={[
                  {
                    key: "type",
                    name: "Transaction type",
                    fieldName: "label",
                    minWidth: 150,
                    maxWidth: 150
                  },
                  {
                    key: "toggle",
                    name: "Available for synchronisation",
                    minWidth: 200,
                    maxWidth: 200,
                    onRender: (item: TransactionTypeRow) => (
                      <ToggleField
                        name={item.key}
                        offText="Excluded"
                        onText="Included"
                        disabled={
                          item.key === "includePayments" &&
                          !values.includeInvoices
                        }
                        styles={{ root: { marginBottom: 0 } }}
                      />
                    )
                  }
                ]}
              />
            </Stack>
            <FieldSpy
              name="includeInvoices"
              onChange={value => {
                if (!value) form.change("includePayments", false);
              }}
            />
          </Stack>
        );
      }}
    </SubmissionForm>
  );
});
